import axios from 'axios';
import { accountManager } from '../../utils/accountManager';

/**
 * Shell HTTP client (axios) for shell-level cross-cutting services that remain
 * in the shell: auth, partnerships, and partner-guests.
 *
 * AUTH-PATH NOTE: This client reads its credentials from the SAME source as the
 * plugin-sdk `getApiHeaders()` fetch path used by UI plugins. Both read the
 * `accounts` + `activeAccountEmail` localStorage keys (via accountManager here,
 * and inline in @lognai/plugin-sdk's api-client.ts) and emit the same header:
 *   - `Authorization: Bearer <session.accessToken>`
 * The only differences are transport (axios vs fetch) and that this client adds
 * a 401 -> clear-session -> redirect-to-/login interceptor. The token sources are
 * already aligned, so no auth change is required for the remaining shell services.
 */

/**
 * Endpoints a caller reaches WITHOUT a session. They get no stored
 * credentials on the way out (a stale or oversized token must never be able
 * to break signing in — 2026-09-11, an ~11KB token 520'd every request
 * including the login POST, so users could not recover on their own), and a
 * 401 from them means wrong credentials, not an expired session.
 */
export const PUBLIC_AUTH_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/refresh',
  // The second half of a sign-in: the caller has proved a password but holds
  // no session yet, and a 401 here means the code was wrong.
  '/api/auth/2fa/challenge',
];

/**
 * Session-backed auth endpoints whose 401 means "wrong credential", not
 * "your session is over".
 *
 * These are NOT in PUBLIC_AUTH_PATHS, and the difference is load-bearing: that
 * list also strips the Authorization header, and every one of these needs it.
 * What they share with a public route is only the meaning of a 401 — a mistyped
 * two-factor code or password must not tear the session down. Clearing storage
 * mid-challenge throws the ticket away, so one wrong digit used to send the
 * user back to the password form with nothing to explain why.
 *
 * `/api/auth/2fa/challenge` is deliberately absent: it runs BEFORE a session
 * exists, so it belongs in PUBLIC_AUTH_PATHS proper. `/2fa/status` is absent
 * too, for the opposite reason — it is an ordinary session-backed read, and a
 * 401 from it really does mean the session is gone.
 */
const CREDENTIAL_CHECK_PATHS = [
  '/api/auth/2fa/enable',
  '/api/auth/2fa/disable',
  '/api/auth/2fa/backup-codes',
  '/api/auth/change-password',
];

/** Path of a URL, without origin or query. */
const pathOf = (url: string): string =>
  url.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0] ?? '';

const isCredentialCheckUrl = (url?: string): boolean =>
  !!url && CREDENTIAL_CHECK_PATHS.includes(pathOf(url));

export const isPublicAuthUrl = (url?: string): boolean => {
  if (!url) return false;
  // Match the path exactly (ignoring origin and query), so a sibling route like
  // /api/auth/login-history is not mistaken for the public login endpoint.
  const path = pathOf(url);
  return PUBLIC_AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
};

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:4611',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  if (isPublicAuthUrl(config.url)) {
    return config;
  }

  const session = accountManager.getActiveSession();

  // Debug logging for development
  if (import.meta.env.DEV) {
    console.debug('[API Client] Session check:', {
      hasSession: !!session,
      activeEmail: accountManager.getActiveAccountEmail(),
      allAccounts: Object.keys(accountManager.getAllAccounts()),
      accessToken: session?.accessToken ? `${session.accessToken.substring(0, 20)}...` : null,
    });
  }

  // The token is the whole credential. The company the caller acts in is
  // the token's companyId (switch-company mints a new token); the server
  // never read the X-Company-ID header this client used to add.
  if (session?.accessToken) {
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }

  // Debug logging for development
  if (import.meta.env.DEV) {
    console.debug('[API Client] Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      hasToken: !!config.headers.Authorization,
    });
  }

  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Debug logging for development
    if (import.meta.env.DEV && error.response) {
      console.error('[API Client] Request failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        errorData: error.response?.data,
      });
    }

    if (error.response?.status === 401) {
      // Don't redirect for public auth endpoints — a 401 there means wrong
      // credentials (or an invalid reset token), not an expired session.
      // The catch block in each authApi method handles these gracefully.
      if (!isPublicAuthUrl(error.config?.url) && !isCredentialCheckUrl(error.config?.url)) {
        // This session is over (expired, revoked, or the account was
        // deactivated). Drop only THIS account — the others are separate
        // sessions with their own tokens; removeAccount promotes the next one.
        const email = accountManager.getActiveAccountEmail();
        if (email) {
          accountManager.removeAccount(email);
        } else {
          accountManager.clearAll();
        }
        if (accountManager.getAllAccountsList().length > 0) {
          window.location.reload();
        } else {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
